import Api from '../../api.js'
import store from '~/store/store'
import cloneDeep from 'lodash/cloneDeep'

const state = {
  currentFileID: null,
  feedbacks: [],
  cursor: '',
  nextCursor: '',
  repliesCursor: {},
  nextRepliesCursor: {},
  resultsPerPage: 10,
  sortOrder: 'DESC',
  loading: false,
  count: 0
}

function getFeedback (state, feedbackId) {
  const index = state.feedbacks.findIndex(f => f.feedback.feedback_id === feedbackId)
  let feedbackElem = (index !== -1) ? state.feedbacks[index] : null
  return { index, feedbackElem }
}

const getters = {
  feedbacks: state => state.feedbacks,
  feedbacksCount: state => state.count,
  cursor: state => state.cursor,
  isLoading: state => state.loading,
  currentFileID: state => state.currentFileID,
  formattedFeedback: () => payload => {
    let provisionalFeedback = { ...payload, created_at: new Date() }
    if (payload.type === 'score') {
      provisionalFeedback.score_data = payload.data
    } else {
      provisionalFeedback.generic_data = payload.data
    }
    delete provisionalFeedback.data
    return {
      feedback: provisionalFeedback,
      account: {
        account_id: store.state.session.account.account_id,
        account_uuid: store.state.session.accountUUID,
        firstname: store.state.session.account.firstname,
        lastname: store.state.session.account.lastname,
        avatar: store.state.session.account.avatar
      }
    }
  },
  getFeedback: state => feedbackId => getFeedback(state, feedbackId)
}

const mutations = {
  setCurrentFileID (state, fileID) {
    state.currentFileID = fileID
  },
  setFeedbacks (state, payload) {
    state.feedbacks = payload
  },
  addFeedback (state, payload) {
    payload = getters.formattedFeedback()(payload)
    if (payload.feedback.parent_feedback_id) {
      const { index, feedbackElem } = getFeedback(state, payload.feedback.parent_feedback_id)
      const repliesIds = (feedbackElem.feedback.replies || []).map(r => r.feedback.feedback_id)

      if (!feedbackElem.feedback.replies || !repliesIds.includes(payload.feedback.feedback_id)) {
        if (!feedbackElem.feedback.replies) {
          feedbackElem.feedback.replies = []
        }

        feedbackElem.feedback.replies.unshift(payload)
        feedbackElem.feedback.replies_count = feedbackElem.feedback.replies_count + 1
        state.feedbacks.splice(index, 1, feedbackElem)
      }
    } else {
      state.feedbacks.unshift(payload)
      state.count = state.count + 1
    }
  },
  updateFeedback (state, payload) {
    const { index } = getFeedback(state, payload.feedback_id)
    state.feedbacks.splice(index, 1, payload)
  },
  deleteFeedback (state, { feedbackId, feedbackParentId }) {
    if (feedbackParentId) {
      const { index, feedbackElem } = getFeedback(state, feedbackParentId)
      const replyIndex = feedbackElem.feedback.replies.findIndex(fb => fb.feedback.feedback_id === feedbackId)
      feedbackElem.feedback.replies.splice(replyIndex, 1)
      feedbackElem.feedback.replies_count = feedbackElem.feedback.replies_count - 1
      state.feedbacks.splice(index, 1, feedbackElem)
    } else {
      const { index } = getFeedback(state, feedbackId)
      state.feedbacks.splice(index, 1)
      state.count = state.count - 1
    }
  },
  setCursor (state, payload) {
    state.cursor = payload
  },
  setNextCursor (state, payload) {
    state.nextCursor = payload
  },
  setRepliesCursor (state, { feedbackId, cursor }) {
    state.repliesCursor = {
      ...state.repliesCursor,
      [feedbackId]: cursor
    }
  },
  setNextRepliesCursor (state, { feedbackId, cursor }) {
    state.nextRepliesCursor = {
      ...state.nextRepliesCursor,
      [feedbackId]: cursor
    }
  },
  setReplies (state, { feedbackId, replies, count }) {
    const { index, feedbackElem } = getFeedback(state, feedbackId)
    feedbackElem.feedback.replies = replies
    feedbackElem.feedback.replies_count = count
    state.feedbacks.splice(index, 1, feedbackElem)
  },
  clearAllRepliesCursors (state) {
    state.repliesCursor = {}
    state.nextRepliesCursor = {}
  },
  setLoading (state, flag) {
    state.loading = flag
  },
  setSortOrder (state, sortOrder) {
    state.sortOrder = sortOrder
  },
  setCount (state, count) {
    if (typeof count === 'number') {
      state.count = count
    } else {
      state.count = 0
    }
  }
}

const actions = {
  async loadFeedbacks ({ commit, state, dispatch }, { fileID }) {
    // If fileID changes, clear existing data and load from scratch
    if (state.currentFileID !== fileID) {
      commit('setCurrentFileID', fileID)
      commit('setFeedbacks', [])
      commit('setCursor', null)
      commit('setNextCursor', '')
    }

    commit('setLoading', true)

    try {
      // Build the endpoint
      const endpoint = `file/${fileID}/feedback`

      // Prepare data for the request
      const data = {
        pagination: {
          limit: state.resultsPerPage,
          cursor: state.cursor,
          sort_direction: state.sortOrder
        }
      }

      const response = await Api.postV2(endpoint, { data }).then(resp => resp.body)
      commit('setFeedbacks', response.feedbacks || [])
      commit('setNextCursor', response.cursor)
      commit('setCount', response.count)

      return response.feedbacks || []
    } catch (error) {
      dispatch('feedback/addCatchSnack', error, {root: true})
      throw error
    } finally {
      commit('setLoading', false)
    }
  },

  async createFeedback ({ commit, dispatch }, { fileID, feedbackData }) {
    try {
      const endpoint = `file/${fileID}/add-feedback`
      const response = await Api.postV2(endpoint, { data: feedbackData }).then(resp => resp.body)

      commit('addFeedback', {...feedbackData, feedback_id: response.id})

      return response
    } catch (error) {
      dispatch('feedback/addCatchSnack', error, {root: true})
    }
  },

  async updateFeedback ({ commit, dispatch }, { fileID, feedbackId, feedbackData }) {
    try {
      const endpoint = `file/${fileID}/feedback/${feedbackId}`
      await Api.putV2(endpoint, { data: feedbackData }).then(resp => resp.body)

            // Update local data
      commit('updateFeedback', { ...feedbackData, feedback_id: feedbackId })
    } catch (error) {
      dispatch('feedback/addCatchSnack', error, {root: true})
    }
  },

  async deleteFeedback ({ commit, dispatch }, { fileID, feedbackId, feedbackParentId }) {
    try {
      const endpoint = `file/${fileID}/feedback/${feedbackId}`
      await Api.deleteV2(endpoint).then(resp => resp.body)

      commit('deleteFeedback', { feedbackId, feedbackParentId })
    } catch (error) {
      dispatch('feedback/addCatchSnack', error, {root: true})
    }
  },

  async loadReplies ({ commit, dispatch }, { feedbackId }) {
    try {
      const endpoint = `feedback/${feedbackId}/replies`
      const data = {
        pagination: {
          limit: state.resultsPerPage,
          cursor: state.repliesCursor[feedbackId] || null,
          sort_direction: state.sortOrder
        }
      }
      commit('setLoading', true)
      const response = await Api.postV2(endpoint, { data }).then(resp => resp.body)
      commit('setNextRepliesCursor', { feedbackId, cursor: response.cursor })
      commit('setReplies', { feedbackId, replies: response.feedbacks, count: response.count })
    } catch (error) {
      dispatch('feedback/addCatchSnack', error, {root: true})
    } finally {
      commit('setLoading', false)
    }
  },
  clearFeedbacks ({ commit }) {
    commit('setFeedbacks', [])
    commit('setCursor', '')
    commit('setNextCursor', '')
    commit('clearAllRepliesCursors')
    commit('setCount', 0)
  },
  clearReplies ({ commit }, { feedbackId }) {
    commit('setRepliesCursor', { feedbackId, cursor: '' })
    commit('setNextRepliesCursor', { feedbackId, cursor: '' })
  },
  async changeSortOrder ({ commit, dispatch, state }, newSortOrder) {
    // Only process if sort order actually changes
    if (state.sortOrder !== newSortOrder) {
      // Update sort order
      commit('setSortOrder', newSortOrder)
      // Clear all cursors and data
      dispatch('clearFeedbacks')
      // Reload data if there's a current file selected
      if (state.currentFileID) {
        await dispatch('loadFeedbacks', { fileID: state.currentFileID })
      }
    }
  },
  async loadMoreFeedback ({ commit, dispatch, state }) {
    if (state.nextCursor !== '') {
      const feedbacks = cloneDeep(state.feedbacks)
      commit('setCursor', state.nextCursor)
      await dispatch('loadFeedbacks', { fileID: state.currentFileID })
      commit('setFeedbacks', [...feedbacks, ...state.feedbacks])
    }
  },
  async loadMoreReplies ({ commit, dispatch, state, getters }, { feedbackId }) {
    if (state.nextRepliesCursor[feedbackId] !== '') {
      const {index, feedbackElem} = getters.getFeedback(feedbackId)
      const feedbacksReplies = cloneDeep(feedbackElem.feedback.replies || [])
      commit('setRepliesCursor', { feedbackId, cursor: state.nextRepliesCursor[feedbackId] })
      await dispatch('loadReplies', { feedbackId })
      commit('setReplies', { feedbackId, replies: [...feedbacksReplies, ...state.feedbacks[index].feedback.replies], count: state.feedbacks[index].feedback.replies_count })
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/idonia-api/files/feedback/feedback.js